// A NAV törzsadat megjelenítendő mezői – a FORRÁS szerint eltérő szerkezet közös nevezőre hozva.
//  • 'upo'  → NAV Ügyfélportál „Saját adatok": a HITELESÍTETT PDF-ből kibontott gazdag táblák +
//    a portál strukturált JSON-ja. A fejléc-mezőket NEM drótozzuk be: a `fontos` tömb már
//    címke/érték párokban jön a NAV-tól (label+sorrend), így ha a NAV bővíti, magától megjelenik.
//  • 'ebev' → a NAV „Szervezet törzsadat lekérdezés" PDF-je (KIVEZETÉS ALATT – legvégső tartalék).
//  • 'osa'  → az Online Számla queryTaxpayer részhalmaza (név/cím/áfa-státusz/típus).
// Így a Törzsadatok (egyszeres) és a Kettős oldal UGYANAZT a táblát rendereli.

export interface TorzsMezo { cimke: string; ertek: string; }

// A források emberi neve – EGY helyen, hogy a Törzsadatok és a Kettős oldal ne csússzon szét.
export function torzsForrasNev(f: string): string {
  return f === 'upo' ? 'NAV Ügyfélportál – hitelesített „Saját adatok”'
    : f === 'ebev' ? 'NAV eBEV – Szervezet törzsadat lekérdezés (kivezetés alatt)'
    : f === 'osa' ? 'NAV Online Számla (queryTaxpayer)'
    : (f || '—');
}

function adozoTipus(t: string): string {
  return t === 'ceg' ? 'Cég / szervezet'
    : t === 'egyeni' ? 'Egyéni vállalkozó'
    : t === 'maganszemely' ? 'Adószámos magánszemély'
    : (t || '—');
}

export function torzsadatMezok(t: any): TorzsMezo[] {
  if (!t) return [];
  const sor = (cimke: string, ertek: any): TorzsMezo | null =>
    (ertek !== null && ertek !== undefined && String(ertek).trim() !== '' ? { cimke, ertek: String(ertek) } : null);

  // ÜPO: a NAV saját címkéivel, a NAV sorrendjében – plusz amit a PDF-ből nyertünk.
  if (t.forras === 'upo') {
    const fontos: TorzsMezo[] = (t.fontos || [])
      .slice()
      .sort((a: any, b: any) => (a?.sorrend ?? 0) - (b?.sorrend ?? 0))
      .map((x: any) => sor(String(x?.label || '').replace(/,\s*$/, ''), x?.value))
      .filter(Boolean) as TorzsMezo[];
    return [
      ...fontos,
      ...[
        sor('Gazdálkodási forma (GFO)', t.gazdalkodasi_forma),
        sor('Adózó típusa', adozoTipus(t.tipus)),
        sor('NAV riport azonosító', t.riport),
      ].filter(Boolean) as TorzsMezo[],
    ];
  }

  const lista = t.forras === 'ebev'
    ? [
        sor('Adószám', t.adoszam),
        sor('Hivatalos megnevezés', t.nev),
        sor('Adózói minősítés', t.minosites),
        sor('Gazdálkodási forma', t.gazdalkodasi_forma),
        sor('Cég-nyilvántartási szám', t.cegjegyzekszam),
        sor('Közösségi adószám', t.kozossegi_adoszam),
        sor('Jegyzett tőke', t.jegyzett_toke),
        sor('Tevékenység kezdése', t.tevekenyseg_kezdes),
        sor('Illetékes igazgatóság', t.igazgatosag),
        sor('Bevallói típuskód', t.bevallo_tipus),
        sor('Összesítő nyilatkozat', t.osszesito_nyilatkozat),
        sor('Cégkapu nyitásra kötelezett', t.cegkapu),
        sor('Elektronikus kapcsolattartás', t.e_kapcsolattartas),
        sor('KIVA', t.kiva),
        sor('Leválogatás dátuma', t.levalogatas),
        sor('NAV riport azonosító', t.riport),
      ]
    : [
        sor('Adószám', t.adoszam),
        sor('Név', t.nev),
        sor('Rövid név', t.rovid_nev),
        sor('Adózó típusa', adozoTipus(t.tipus)),
        sor('ÁFA-csoport tagság', t.vatGroup),
      ];
  return lista.filter(Boolean) as TorzsMezo[];
}

export function torzsCimTipus(t: string): string {
  const u = String(t || '').toUpperCase();
  return u === 'HQ' ? 'Székhely' : u === 'SITE' ? 'Telephely' : u === 'BRANCH' ? 'Fióktelep' : (t || 'Cím');
}

// A törzsadat TÁBLÁZATOS szekciói egységes alakban (cím + fejléc + sorok), hogy a felület
// generikusan renderelje őket. eBEV → a NAV PDF-jéből kiolvasott teljes tartalom; OSA → csak címek.
export interface TorzsTabla { cim: string; fejlec: string[]; sorok: string[][]; }

export function torzsadatTablak(t: any): TorzsTabla[] {
  if (!t) return [];
  const out: TorzsTabla[] = [];
  const add = (cim: string, fejlec: string[], arr: any[], map: (x: any) => any[]) => {
    if (Array.isArray(arr) && arr.length) out.push({ cim, fejlec, sorok: arr.map((x) => map(x).map((v) => (v == null || v === '' ? '—' : String(v)))) });
  };

  if (t.forras === 'upo') {
    add('Tevékenységek (TEÁOR)', ['Kód', 'Megnevezés', 'Típus', 'Érvényes'], t.tevekenysegek,
      (x) => [x.kod, x.megnevezes, x.tipus, x.ervenyes]);
    add('Cím adatok', ['Típus', 'Cím', 'Érvényesség kezdete'], t.cimek,
      (x) => [x.tipus, x.cim, x.kezdete]);
    add('Bankszámlaszámok', ['Számlaszám', 'Bejelentés forrása', 'Érvényesség kezdete'], t.szamlaszamok,
      (x) => [x.szamlaszam, x.penzintezet, x.datum]);
    add('Képviselők', ['Név', 'Adóazonosító', 'Képviselet jellege', 'Terjedelme', 'Jog kezdete'], t.kepviselok_ceg,
      (x) => [x.nev, x.adoazonosito, x.tipus, x.terjedelme, x.ervenyes]);
    add('Tulajdonosok', ['Név', 'Adóazonosító', 'Lakóhely / székhely', 'Érvényesség kezdete'], t.tulajdonosok,
      (x) => [x.nev, x.adoazonosito, x.cim, x.ervenyes]);
    // A további NAV-szekciók (Áfa-mentesség, KIVA-nyilatkozatok…) generikusan, ahogy a PDF adja:
    // így akkor sem vesztünk adatot, ha a NAV új szekciót vezet be.
    for (const e of (t.egyeb || [])) {
      add(e?.szekcio || 'Egyéb adatok', ['Megnevezés', 'Érték'], e?.adatok || [], (x) => [x.label, x.value]);
    }
    return out;
  }

  if (t.forras === 'ebev') {
    add('Tevékenységek (TEÁOR)', ['Kód', 'Megnevezés', 'Típus', 'Érvényes'], t.tevekenysegek,
      (x) => [x.kod, x.megnevezes, x.tipus, x.ervenyes]);
    add('Cím adatok', ['Típus', 'Cím', 'Érvényesség kezdete', 'Érvényesség vége'], t.cimek,
      (x) => [x.tipus, x.cim, x.kezdete, x.vege]);
    add('Számlaszámok', ['Pénzintézet', 'Számlaszám', 'Bejelentés típusa', 'Bejelentés dátuma'], t.szamlaszamok,
      (x) => [x.penzintezet, x.szamlaszam, x.tipus, x.datum]);
    add('Jellemzők', ['Jellemző neve', 'Kód', 'Megnevezés'], t.jellemzok,
      (x) => [x.nev, x.kod, x.megnevezes]);
    add('Cégbíróságon jegyzett tulajdonosok', ['Adóazonosító', 'Név', 'Cím', 'Érvényesség kelte'], t.tulajdonosok,
      (x) => [x.adoazonosito, x.nev, x.cim, x.ervenyes]);
    add('NAV-nál bejelentett képviselők', ['Típus', 'Név', 'Cím', 'Érvényesség kelte'], t.kepviselok_nav,
      (x) => [x.tipus, x.nev, x.cim, x.ervenyes]);
    add('Cégbíróságon jegyzett képviselők', ['Típus', 'Név', 'Adóazonosító', 'Cím', 'Érvényesség kelte'], t.kepviselok_ceg,
      (x) => [x.tipus, x.nev, x.adoazonosito, x.cim, x.ervenyes]);
  } else {
    // OSA (queryTaxpayer): más cím-szerkezet (irsz/település/cím).
    add('Nyilvántartott címek', ['Típus', 'Irsz.', 'Település', 'Cím'], t.cimek,
      (x) => [torzsCimTipus(x.tipus), x.irsz, x.telepules, x.cim]);
  }
  return out;
}
